import React, { FC } from 'react';

import { Button, Form, Layout, Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { CatalogVisibilityRule, OrderApprovalRule } from '@app/models/products';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { PageTitle } from '@components/PageTitle';

import { FormCatalog, IFormValues } from '../../components/Form';
import { useCreateCatalogQuery } from '../../hooks';
import { ProductModalProvider } from '../../providers';

export const Add: FC = () => {
  const commonT = useTranslation(NSKeys.common);
  const catalogsT = useTranslation(NSKeys.catalogs);
  const validationT = useTranslation(NSKeys.validation);
  const navigate = useNavigate();
  const [form] = Form.useForm<IFormValues>();
  const goBack = () => navigate('/dashboard/catalogs');

  const { mutate: createCatalog, status, failureReason } = useCreateCatalogQuery();

  React.useEffect(() => {
    if (status === 'success') {
      goBack();
    }
  }, [status]);

  const handleSubmit = async (formValues: IFormValues): Promise<void> => {
    const uids = formValues.media?.map((el) => el?.response?.uid);

    createCatalog({
      data: {
        name: formValues.name,
        start_date: formValues.start_date || null,
        end_date: formValues.end_date || null,
        cover_uid: uids?.[0] || null,
        process_uid: formValues?.process_uid || null,
        warehouse_uid: formValues?.warehouse?.value || null,
        ...(formValues.product_uids?.length &&
          formValues?.warehouse?.value && { product_uids: formValues.product_uids }),
        settings: {
          visibility_rule: formValues.visibility_rule,
          order_approval_chain_uid: formValues.order_approval_chain_uid,
          order_approval_rule: formValues.order_approval_rule,
          show_multiplicity_indicator: formValues.show_multiplicity_indicator,
          ...(formValues?.warehouse?.value && { catalog_type: formValues.catalog_type }),
          ...(formValues.visible_to_users?.length && {
            visible_to_user_ids: formValues.visible_to_users.map((u) => u.value).filter((u): u is number => true),
          }),
          ...(formValues.visible_to_user_group_uids?.length && {
            visible_to_user_group_uids: formValues.visible_to_user_group_uids,
          }),
        },
      },
    });
  };

  React.useEffect(() => {
    if (failureReason) {
      const formValues = form.getFieldsValue();
      const { pointerErrors } = getFormErrorsFromResponse(formValues, failureReason, validationT.t);
      if (pointerErrors.length) {
        form.setFields(pointerErrors);
      }
    }
  }, [failureReason]);

  return (
    <>
      <Layout.Header>
        <Breadcrumbs />
        <Row justify="space-between" align="middle">
          <PageTitle title={catalogsT.t('titleAdd')} onClick={goBack} />
          <Space size="middle">
            <Button onClick={goBack} size="middle" name="catalog_form" id="catalog_form">
              {commonT.t('buttonCancel')}
            </Button>
            <Button disabled={status === 'loading'} type="primary" size="middle" form="catalogForm" htmlType="submit">
              {commonT.t('buttonSave')}
            </Button>
          </Space>
        </Row>
      </Layout.Header>
      <ProductModalProvider>
        <FormCatalog
          form={form}
          onFinish={handleSubmit}
          initialValues={{
            order_approval_rule: OrderApprovalRule.NotNeeded,
            visibility_rule: CatalogVisibilityRule.Hidden,
            show_multiplicity_indicator: false,
          }}
        />
      </ProductModalProvider>
    </>
  );
};
